import { gql } from '@apollo/client';
import { useSubscription } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { GenerateActivityReportJobContext } from './context';
import { useResettableMutation } from '../../hooks/useResettableMutation';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';

const START_JOB = gql`
  mutation startActivityReportJob($policyId: ID!, $reportStartDate: Date!, $reportEndDate: Date!, $includeComplianceIntervals: Boolean) {
    generateActivityReport(policyId: $policyId, reportStartDate: $reportStartDate, reportEndDate: $reportEndDate, includeComplianceIntervals: $includeComplianceIntervals)
  }
`;

const ON_GENERATE_ACTIVITY_REPORT_JOB_PROGRESS = gql`
  subscription jobProgress($jobId: ID!) {
    job: generateActivityReportJobProgress(jobId: $jobId) {
      jobId
      filename
      url
      finished
      message
      errors
    }
  }
`;

export default function GenerateActivityReportJobProvider({ children }) {
  const history = useHistory();
  const [jobId, setJobId] = useState(null);
  const [jobErrors, setJobErrors] = useState([]);
  const [finished, setJobFinished] = useState(true);
  const [message, setMessage] = useState(null);
  const [filename, setFilename] = useState(null);
  const [url, setUrl] = useState(null);

  const clearJobId = useCallback(() => {
    setJobId(null);
    setJobErrors([]);
  }, []);

  useEffect(() => {
    if (!!jobId && !!finished) {
      setTimeout(() => clearJobId(), 10000);
    }
  }, [finished, clearJobId, jobId]);

  const [startActivityReportJob, { loading, error, reset }] = useResettableMutation(START_JOB, {
    onCompleted: data => {
      setJobFinished(false);
      setJobId(data?.generateActivityReport);
      history.push(history.location.pathname.replace('/activity-report', ''));
    },
    onError: () => setTimeout(() => reset(), 10000)
  });

  const startGenerateActivityReportJob = data => {
    setJobFinished(false);
    startActivityReportJob({
      variables: {
        policyId: data?.policyId,
        reportStartDate: data?.reportStartDate,
        reportEndDate: data?.reportEndDate,
        includeComplianceIntervals: data?.includeComplianceIntervals
      }
    });
  };

  useSubscription(ON_GENERATE_ACTIVITY_REPORT_JOB_PROGRESS, {
    skip: !jobId,
    variables: { jobId },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const job = data?.job;
      const { message, filename, url, finished, errors } = job;

      setJobErrors(errors);
      setJobFinished(finished);
      setMessage(message);
      setFilename(filename);
      setUrl(url);
    }
  });

  return (
    <GenerateActivityReportJobContext.Provider
      value={{
        startGenerateActivityReportJob,
        jobId,
        clearJobId,
        loading,
        error,
        finished,
        filename,
        url,
        message,
        jobErrors
      }}
    >
      {children}
    </GenerateActivityReportJobContext.Provider>
  );
}
